import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useUpdateProposalGroup from '@/api/mutations/proposals/useUpdateProposalGroup';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import {
  UpdateProposalGroupInput,
  UpdateProposalGroupMutation,
} from '@/gql/graphql';

import { applyProposalGroupUpdate } from './applyProposalGroupUpdate';
import { createOptimisticProposalMutationOptions } from './createOptimisticProposalMutation';

/**
 * Optimistic wrapper around {@link useUpdateProposalGroup}: patches the
 * group's title in the cached proposal in `onMutate` so the rename shows
 * immediately, then rolls back on error. The base hook invalidates the
 * proposal on success, reconciling with server truth.
 */
const useOptimisticUpdateProposalGroup = (args: {
  proposalId: string;
  options?: UseMutationOptions<
    UpdateProposalGroupMutation,
    unknown,
    UpdateProposalGroupInput
  >;
}) => {
  const { proposalId, options } = args;
  const queryClient = useQueryClient();
  const queryKey = proposalKeys.proposal(proposalId);

  return useUpdateProposalGroup({
    proposalId,
    options: {
      ...options,
      ...createOptimisticProposalMutationOptions({
        queryClient,
        queryKey,
        apply: applyProposalGroupUpdate,
        options,
      }),
    },
  });
};

export default useOptimisticUpdateProposalGroup;
